import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { getCurrencyFlag, splitCurrenciesByPopularity } from '../lib/currencyUtils';
import { DEFAULT_USER_TYPES, DEFAULT_PERMISSION_CATEGORIES } from '../lib/onboardingDefaults';
import { 
  ArrowRightIcon,
  ArrowLeftIcon,
  CheckIcon,
  LockIcon,
  XIcon
} from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';
import { toast } from 'react-toastify';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const OnboardingFullPage = ({ user }) => {
  console.log('🎯 OnboardingFullPage rendered', { user, BACKEND_URL });
  
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0); // Start with welcome screen (0)
  const [loading, setLoading] = useState(false);
  // "Sayfadan ayrılıyorsunuz — Emin misiniz?" modal state.
  // `pendingAction` çıkışın nasıl gerçekleşeceğini tutar:
  //   - 'browser-back' → popstate ile tetiklendi, EVET'e basınca history.back()
  //   - 'logout' → kullanıcı topbar'daki çıkış butonuna bastı
  //   - 'navigate:<path>' → programatik yönlendirme talebi
  const [leaveModal, setLeaveModal] = useState({ open: false, pendingAction: null });
  
  // Form data - use company's base_currency as default if available
  const [baseCurrency, setBaseCurrency] = useState(user?.company?.base_currency || 'TRY');
  const [extraCurrencies, setExtraCurrencies] = useState([]);
  
  // Personal info
  const [fullName, setFullName] = useState(user?.full_name || '');
  const [email] = useState(user?.email || ''); // Read-only
  const [phoneCountryCode, setPhoneCountryCode] = useState('+90');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [phoneCodes, setPhoneCodes] = useState([]);
  
  // Address info - should be empty, user will fill it in onboarding
  const [address, setAddress] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('TR');
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);
  
  // Corporate invoice fields - pre-fill if company registered with company info
  const hasCompanyInfo = user?.company?.company_name && user?.company?.company_name.trim() !== '';
  const [corporateInvoice, setCorporateInvoice] = useState(hasCompanyInfo);
  const [invoiceCompanyName, setInvoiceCompanyName] = useState(user?.company?.company_name || '');
  const [invoiceTaxNumber, setInvoiceTaxNumber] = useState(user?.company?.tax_number || '');
  const [useSameAddress, setUseSameAddress] = useState(true);
  const [invoiceAddress, setInvoiceAddress] = useState('');
  const [invoicePostalCode, setInvoicePostalCode] = useState('');
  const [invoiceCity, setInvoiceCity] = useState('');
  const [invoiceCountry, setInvoiceCountry] = useState('TR');
  const [invoiceCities, setInvoiceCities] = useState([]);
  const [loadingInvoiceCities, setLoadingInvoiceCities] = useState(false);
  
  // Currency data
  const [currencies, setCurrencies] = useState([]);
  const [currencySearch, setCurrencySearch] = useState('');
  const [extraCurrencySearch, setExtraCurrencySearch] = useState('');
  
  // User invitation data
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('');
  const [roles, setRoles] = useState([]);
  const [invitedUsers, setInvitedUsers] = useState([]);
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState('');

  // Check if Starter plan
  const isStarter = () => {
    const plan = user?.company?.selected_plan || user?.company?.subscription_plan;
    return plan === 'starter';
  };

  // Sayfadan Ayrılma Koruması: bypass bayrağı ile popstate loop önlenir.
  const bypassGuardRef = useRef(false);

  // Draft persistence — kullanıcı Kaydet ve Çık'a bastığında tüm form
  // state'ini localStorage'a yazar; onboarding'e geri dönünce restore edilir.
  const DRAFT_KEY = `full-onboarding-draft:${user?.id || user?.email || 'anon'}`;

  // Restore draft on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!raw) return;
      const d = JSON.parse(raw);
      if (typeof d.currentStep === 'number') setCurrentStep(d.currentStep);
      if (d.baseCurrency) setBaseCurrency(d.baseCurrency);
      if (Array.isArray(d.extraCurrencies)) setExtraCurrencies(d.extraCurrencies);
      if (d.fullName) setFullName(d.fullName);
      if (d.phoneCountryCode) setPhoneCountryCode(d.phoneCountryCode);
      if (d.phoneNumber) setPhoneNumber(d.phoneNumber);
      if (d.address) setAddress(d.address);
      if (d.postalCode) setPostalCode(d.postalCode);
      if (d.city) setCity(d.city);
      if (d.country) setCountry(d.country);
      if (typeof d.corporateInvoice === 'boolean') setCorporateInvoice(d.corporateInvoice);
      if (d.invoiceCompanyName) setInvoiceCompanyName(d.invoiceCompanyName);
      if (d.invoiceTaxNumber) setInvoiceTaxNumber(d.invoiceTaxNumber);
      if (typeof d.useSameAddress === 'boolean') setUseSameAddress(d.useSameAddress);
      if (d.invoiceAddress) setInvoiceAddress(d.invoiceAddress);
      if (d.invoicePostalCode) setInvoicePostalCode(d.invoicePostalCode);
      if (d.invoiceCity) setInvoiceCity(d.invoiceCity);
      if (d.invoiceCountry) setInvoiceCountry(d.invoiceCountry);
      toast.info('Kaldığınız yerden devam ediyorsunuz.', { autoClose: 2500 });
    } catch (e) { /* ignore bozuk JSON */ }
  }, []);

  const saveDraftAndLeave = () => {
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({
        currentStep, baseCurrency, extraCurrencies,
        fullName, phoneCountryCode, phoneNumber,
        address, postalCode, city, country,
        corporateInvoice, invoiceCompanyName, invoiceTaxNumber,
        useSameAddress, invoiceAddress, invoicePostalCode, invoiceCity, invoiceCountry,
        savedAt: new Date().toISOString(),
      }));
      toast.success('İlerlemeniz kaydedildi. Tekrar giriş yaptığınızda kaldığınız yerden devam edeceksiniz.');
    } catch (e) {
      toast.warn('Kayıt sırasında bir sorun oluştu, yine de çıkılıyor.');
    }
    setLeaveModal({ open: false, pendingAction: null });
    bypassGuardRef.current = true;
    setTimeout(() => {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }, 300);
  };

  useEffect(() => {
    const beforeUnloadHandler = (e) => {
      e.preventDefault();
      e.returnValue = 'Onboarding turu tamamen bitmedi ve sayfadan ayrılmak istiyorsunuz. Emin misiniz?';
      return e.returnValue;
    };
    const pushGuard = () => {
      window.history.pushState({ onboardingGuard: true }, '', window.location.href);
    };
    const popStateHandler = () => {
      if (bypassGuardRef.current) {
        bypassGuardRef.current = false;
        return;
      }
      pushGuard();
      setLeaveModal({ open: true, pendingAction: 'browser-back' });
    };
    window.addEventListener('beforeunload', beforeUnloadHandler);
    window.addEventListener('popstate', popStateHandler);
    pushGuard();
    return () => {
      window.removeEventListener('beforeunload', beforeUnloadHandler);
      window.removeEventListener('popstate', popStateHandler);
    };
  }, []);

  const confirmLeave = () => {
    const { pendingAction } = leaveModal;
    setLeaveModal({ open: false, pendingAction: null });
    bypassGuardRef.current = true;
    if (pendingAction === 'browser-back') {
      window.history.back();
      setTimeout(() => {
        localStorage.removeItem('token');
        window.location.href = '/login';
      }, 50);
    } else if (pendingAction === 'logout') {
      localStorage.removeItem('token');
      window.location.href = '/login';
    } else if (typeof pendingAction === 'string' && pendingAction.startsWith('navigate:')) {
      const path = pendingAction.replace('navigate:', '');
      window.location.href = path;
    }
  };
  const cancelLeave = () => setLeaveModal({ open: false, pendingAction: null });

  // Steps configuration
  const allSteps = [
    { id: 1, title: 'Temel Para Birimi', required: true },
    { id: 2, title: 'Ekstra Para Birimleri', required: false },
    { id: 3, title: 'Şirket Bilgileri', required: true },
    { id: 4, title: 'Kullanıcı Ekleme', required: false },
    { id: 5, title: 'Kullanıcı Tipleri', required: false },
    { id: 6, title: 'Pozisyon Hiyerarşisi', required: false, hideForStarter: true },
    { id: 7, title: 'Kullanıcı Yetkileri', required: false }
  ];

  const steps = allSteps.filter(step => !step.hideForStarter || !isStarter());
  const totalSteps = steps.length;

  // Load currencies
  useEffect(() => {
    const fetchCurrencies = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${BACKEND_URL}/api/currencies`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCurrencies(response.data || []);
      } catch (error) {
        console.error('Error loading currencies:', error);
      }
    };
    fetchCurrencies();
  }, []);

  // Load roles for user invitation
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${BACKEND_URL}/api/roles`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setRoles(response.data || []);
        // Set default role to first non-admin role
        const defaultRole = response.data.find(r => r.name !== 'Süper Admin');
        if (defaultRole) setInviteRole(defaultRole.id);
      } catch (error) {
        console.error('Error loading roles:', error);
      }
    };
    fetchRoles();
  }, []);

  // Load phone codes
  useEffect(() => {
    const fetchPhoneCodes = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${BACKEND_URL}/api/country-phone-codes`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPhoneCodes(response.data || []);
      } catch (error) {
        console.error('Error loading phone codes:', error);
      }
    };
    fetchPhoneCodes();
  }, []);

  // Load countries on mount
  useEffect(() => {
    console.log('🔥 OnboardingFullPage: Countries useEffect triggered!');
    
    const fetchCountries = async () => {
      try {
        console.log('📍 Fetching countries from:', `${BACKEND_URL}/api/countries`);
        const response = await axios.get(`${BACKEND_URL}/api/countries`);
        console.log('✅ Countries loaded:', response.data?.length);
        setCountries(response.data || []);
      } catch (error) {
        console.error('❌ Error loading countries:', error);
        setCountries([]);
      }
    };
    
    fetchCountries();
  }, []);

  // Load cities when country changes
  useEffect(() => {
    console.log('🏙️ OnboardingFullPage: Cities useEffect triggered for country:', country);
    
    if (!country) {
      console.log('⏭️ No country selected, skipping cities fetch');
      setCities([]);
      return;
    }
    
    const fetchCities = async () => {
      setLoadingCities(true);
      try {
        const url = `${BACKEND_URL}/api/cities?country=${country}`;
        console.log('📍 Fetching cities from:', url);
        const response = await axios.get(url);
        console.log('✅ Cities loaded:', response.data?.length);
        setCities(response.data || []);
      } catch (error) {
        console.error('❌ Error loading cities:', error);
        setCities([]);
      } finally {
        setLoadingCities(false);
      }
    };
    
    fetchCities();
  }, [country]);

  // Load invoice cities when invoice country changes
  useEffect(() => {
    if (!invoiceCountry) {
      setInvoiceCities([]);
      return;
    }
    
    setLoadingInvoiceCities(true);
    
    axios.get(`${BACKEND_URL}/api/cities?country=${invoiceCountry}`)
      .then(response => {
        setInvoiceCities(response.data || []);
        setLoadingInvoiceCities(false);
      })
      .catch(error => {
        console.error('❌ Error loading invoice cities:', error);
        setInvoiceCities([]);
        setLoadingInvoiceCities(false);
      });
  }, [invoiceCountry]);

  // Phone number formatting functions
  const formatPhoneNumber = (value) => {
    // Remove all non-digit characters
    const digits = value.replace(/\D/g, '');
    
    // Format as XXX XXX XX XX
    if (digits.length <= 3) {
      return digits;
    } else if (digits.length <= 6) {
      return `${digits.slice(0, 3)} ${digits.slice(3)}`;
    } else if (digits.length <= 8) {
      return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
    } else {
      return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 8)} ${digits.slice(8, 10)}`;
    }
  };

  const validatePhoneNumber = (value) => {
    // Remove spaces
    const digits = value.replace(/\s/g, '');
    
    // Check if only digits
    if (!/^\d+$/.test(digits)) {
      return 'Telefon numarası sadece rakam içermelidir';
    }
    
    // Check length (10 digits for Turkish format)
    if (digits.length !== 10) {
      return 'Telefon numarası 10 haneli olmalıdır';
    }
    
    return '';
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    const formatted = formatPhoneNumber(value);
    setPhoneNumber(formatted);
    setPhoneError(''); // Clear error while typing
  };

  const handlePhoneBlur = () => {
    if (phoneNumber) {
      const error = validatePhoneNumber(phoneNumber);
      setPhoneError(error);
    }
  };

  const handleNext = () => {
    // Validation
    const currentStepData = steps[currentStep - 1];
    
    if (currentStepData.id === 1 && !baseCurrency) {
      toast.error('Lütfen temel para biriminizi seçin');
      return;
    }
    
    if (currentStepData.id === 3) {
      // Kişisel bilgiler validation
      if (!fullName.trim()) {
        toast.error('Adı Soyadı zorunludur');
        return;
      }
      if (!phoneNumber.trim()) {
        toast.error('Telefon numarası zorunludur');
        return;
      }
      
      // Validate phone format
      const phoneValidationError = validatePhoneNumber(phoneNumber);
      if (phoneValidationError) {
        setPhoneError(phoneValidationError);
        toast.error('Telefon numarası formatı hatalı');
        return;
      }
      
      // Kurumsal fatura validation
      if (corporateInvoice) {
        if (!invoiceCompanyName.trim()) {
          toast.error('Fatura şirket adı zorunludur');
          return;
        }
        if (!invoiceTaxNumber.trim()) {
          toast.error('Vergi numarası zorunludur');
          return;
        }
        if (invoiceTaxNumber.length !== 10) {
          toast.error('Vergi numarası tam olarak 10 basamak olmalıdır');
          return;
        }
        
        // Farklı adres kullanılıyorsa validation
        if (!useSameAddress) {
          if (!invoiceAddress.trim()) {
            toast.error('Fatura adresi zorunludur');
            return;
          }
          if (!invoicePostalCode.trim()) {
            toast.error('Fatura posta kodu zorunludur');
            return;
          }
          if (!invoiceCity.trim()) {
            toast.error('Fatura şehri zorunludur');
            return;
          }
        }
      }
    }
    
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkipStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handleInviteUser = async () => {
    // Clear previous error
    setInviteError('');
    
    if (!inviteEmail || !inviteRole) {
      const errorMsg = 'Lütfen e-posta ve rol seçin';
      setInviteError(errorMsg);
      toast.error(errorMsg);
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(inviteEmail)) {
      const errorMsg = 'Geçerli bir e-posta adresi girin';
      setInviteError(errorMsg);
      toast.error(errorMsg);
      return;
    }

    // Check if email already invited in this session
    if (invitedUsers.some(u => u.email.toLowerCase() === inviteEmail.toLowerCase())) {
      const errorMsg = 'Bu e-posta adresine zaten davet gönderdiniz!';
      setInviteError(errorMsg);
      toast.error(errorMsg);
      return;
    }

    setInviting(true);
    try {
      const token = localStorage.getItem('token');
      
      // Use /users/invite endpoint to send invitation
      await axios.post(
        `${BACKEND_URL}/api/users/invite`,
        {
          email: inviteEmail,
          role_id: inviteRole,
          manager_id: null // No manager assignment during onboarding
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Add to invited list
      const roleName = roles.find(r => r.id === inviteRole)?.name || 'Kullanıcı';
      setInvitedUsers([...invitedUsers, { email: inviteEmail, role: roleName }]);
      
      toast.success(`📧 Davet e-postası ${inviteEmail} adresine gönderildi!`);
      
      // Clear form and error
      setInviteEmail('');
      setInviteError('');
    } catch (error) {
      console.error('Invite error:', error);
      
      // Better error messages in Turkish
      let errorMsg = 'Davet gönderilirken hata oluştu';
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        
        if (detail.includes('already exists') || detail.includes('User already exists')) {
          errorMsg = 'Bu e-posta adresi ile sistemde kayıtlı bir kullanıcı var. Lütfen farklı bir e-posta adresi deneyin.';
        } else if (detail.includes('already invited') || detail.includes('Invitation already sent')) {
          errorMsg = 'Bu e-posta adresine zaten daha önce davet gönderilmiş. Kullanıcı e-postasını kontrol etmelidir.';
        } else if (detail.includes('limit exceeded') || detail.includes('User limit')) {
          errorMsg = 'Kullanıcı limitine ulaşıldı. Lütfen paketinizi yükseltin.';
        } else {
          errorMsg = detail;
        }
      }
      
      setInviteError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setInviting(false);
    }
  };

  const handleComplete = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Prepare company name and tax number
      const finalCompanyName = corporateInvoice ? invoiceCompanyName : fullName;
      const finalTaxNumber = corporateInvoice ? invoiceTaxNumber : '';
      
      // Prepare address
      const finalAddress = useSameAddress || !corporateInvoice 
        ? `${address}, ${postalCode} ${city}, ${country}` 
        : `${invoiceAddress}, ${invoicePostalCode} ${invoiceCity}, ${invoiceCountry}`;
      
      // Save data
      const fullPhoneNumber = `${phoneCountryCode} ${phoneNumber}`;
      
      await axios.post(
        `${BACKEND_URL}/api/onboarding/complete`,
        {
          base_currency: baseCurrency,
          extra_currencies: extraCurrencies,
          company_name: finalCompanyName,
          address: finalAddress,
          tax_number: finalTaxNumber,
          full_name: fullName,  // Send user's full name
          phone_number: fullPhoneNumber  // Send user's phone number with country code
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      toast.success('Kurulum tamamlandı! 🎉');
      // Draft'ı temizle — onboarding başarıyla tamamlandı.
      try { localStorage.removeItem(DRAFT_KEY); } catch (e) { /* ignore */ }
      // Reload to update user data
      window.location.href = '/dashboard';
    } catch (error) {
      console.error('Onboarding error:', error);
      toast.error('Bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  const filteredCurrencies = currencies.filter(c =>
    c.code?.toLowerCase().includes(currencySearch.toLowerCase()) ||
    c.name?.toLowerCase().includes(currencySearch.toLowerCase())
  );

  const filteredExtraCurrencies = currencies
    .filter(c => c.code !== baseCurrency)
    .filter(c =>
      c.code?.toLowerCase().includes(extraCurrencySearch.toLowerCase()) ||
      c.name?.toLowerCase().includes(extraCurrencySearch.toLowerCase())
    );

  const renderStepContent = () => {
    // Welcome screen (step 0)
    if (currentStep === 0) {
      return (
        <div className="flex flex-col justify-between h-full min-h-[500px]">
          <div className="text-center mt-8">
            <div className="text-6xl mb-4">🎉</div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Tebrikler!
            </h2>
            <h3 className="text-xl font-semibold text-teal-600 mb-3">
              AdvanceCash.io Avans Yönetim Süper Admin Paneline Hoş Geldiniz
            </h3>
            {user?.email && (
              <p className="text-gray-600 text-base">
                <span className="font-medium">{user.email}</span>
              </p>
            )}
          </div>

          <div className="flex-grow"></div>

          <div className="max-w-3xl mx-auto px-6 mb-8">
            <p className="text-gray-700 leading-relaxed text-base text-center">
              Şimdi hesabınızı daha iyi kullanabilmemiz için bazı ayarlar yapmamız gerekmektedir. 
              Avans işlemlerinizi sağlıklı bir şekilde yürütebilmek için lütfen öncelikle{' '}
              <span className="font-semibold text-blue-700">temel para biriminizi seçiniz</span>, 
              seçtiğiniz bu para birimi{' '}
              <span className="font-semibold text-red-600">daha sonra değiştirilemez</span>. 
              Ardından avans işlemleri sırasında kullanabileceğiniz temel para biriminizden farklı{' '}
              <span className="font-semibold text-blue-700">4 para birimini</span> de ekleyebilirsiniz. 
              Sonrasında avans yönetim sisteminizi kullanmaya başlayabilir, sisteminize takım arkadaşlarınızı 
              davet edebilir ve masraflarınızı kontrol altına alabilirsiniz.
            </p>
          </div>

          <div className="flex items-center justify-center space-x-4 mb-8">
            <button
              onClick={() => navigate('/dashboard')}
              className="px-6 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors duration-200 flex items-center space-x-2"
            >
              <ArrowLeftIcon className="w-5 h-5" />
              <span>Dashboard'a Dön</span>
            </button>
            <button
              onClick={() => setCurrentStep(1)}
              className="px-8 py-3 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-lg transition-colors duration-200 flex items-center space-x-2"
            >
              <span>Başlayalım</span>
              <ArrowRightIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
      );
    }

    const currentStepData = steps[currentStep - 1];

    switch (currentStepData.id) {
      case 1: // Temel Para Birimi
        return (
          <div className="space-y-4">
            <div className="text-center mb-6">
              <div className="text-5xl mb-3">💰</div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Temel Para Biriminizi Seçin
              </h3>
              <div className="flex items-center justify-center space-x-2 text-amber-600 bg-amber-50 py-2 px-4 rounded-lg inline-flex">
                <LockIcon className="w-4 h-4" />
                <p className="text-sm font-medium">
                  Bu seçim sonradan değiştirilemez!
                </p>
              </div>
            </div>

            <div className="max-w-md mx-auto">
              <input
                type="text"
                placeholder="Para birimi ara... (örn: USD, Euro, Dolar)"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg mb-3"
                value={currencySearch}
                onChange={(e) => setCurrencySearch(e.target.value)}
              />
              
              <div className="border border-gray-300 rounded-lg max-h-64 overflow-y-auto" data-testid="base-currency-list">
                {(() => {
                  const { popular, rest } = splitCurrenciesByPopularity(filteredCurrencies);
                  const renderRow = (currency) => (
                    <div
                      key={currency.code}
                      className={`px-4 py-3 cursor-pointer hover:bg-gray-50 border-b last:border-b-0 ${
                        baseCurrency === currency.code ? 'bg-teal-50 border-l-4 border-l-teal-600' : ''
                      }`}
                      onClick={() => setBaseCurrency(currency.code)}
                      data-testid={`currency-option-${currency.code}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-xl leading-none" aria-hidden>{getCurrencyFlag(currency.code)}</span>
                          <span className="font-semibold text-gray-900">{currency.symbol} {currency.code}</span>
                          <span className="text-gray-600 text-sm">{currency.name}</span>
                        </div>
                        {baseCurrency === currency.code && (
                          <CheckIcon className="w-5 h-5 text-teal-600" />
                        )}
                      </div>
                    </div>
                  );
                  // Search aktifken popüler/diğer ayrımını kaldır — tek liste
                  if (currencySearch.trim()) {
                    return filteredCurrencies.map(renderRow);
                  }
                  return (
                    <>
                      {popular.map(renderRow)}
                      {popular.length > 0 && rest.length > 0 && (
                        <div className="px-4 py-1.5 bg-gray-50 text-[10px] uppercase tracking-wider text-gray-400 font-medium border-b">
                          Diğer para birimleri
                        </div>
                      )}
                      {rest.map(renderRow)}
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        );

      case 2: // Ekstra Para Birimleri
        return (
          <div className="space-y-4">
            <div className="text-center mb-6">
              <div className="text-5xl mb-3">💱</div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Ekstra Para Birimleri (Maksimum 4)
              </h3>
              <p className="text-gray-600 text-sm">
                Avans taleplerinde kullanabileceğiniz ek para birimlerini seçin
              </p>
            </div>

            <div className="max-w-md mx-auto">
              <div className="mb-3 p-3 bg-gray-100 rounded-lg">
                <p className="text-sm text-gray-700">
                  <span className="font-semibold">Temel Para Birimi:</span> {baseCurrency}
                </p>
              </div>

              {/* Seçilen Para Birimleri */}
              {extraCurrencies.length > 0 && (
                <div className="mb-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">Seçilen Para Birimleri:</p>
                  <div className="flex flex-wrap gap-2">
                    {extraCurrencies.map(code => {
                      const curr = currencies.find(c => c.code === code);
                      return (
                        <div key={code} className="bg-teal-100 text-teal-800 px-3 py-1 rounded-full flex items-center space-x-2">
                          <span aria-hidden>{getCurrencyFlag(code)}</span>
                          <span className="font-semibold">{curr?.symbol} {code}</span>
                          <button
                            onClick={() => setExtraCurrencies(extraCurrencies.filter(c => c !== code))}
                            className="hover:bg-teal-200 rounded-full p-0.5"
                          >
                            <XIcon className="w-4 h-4" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-sm text-gray-500 mt-2">
                    {extraCurrencies.length}/4 para birimi seçildi
                  </p>
                </div>
              )}

              <input
                type="text"
                placeholder="Para birimi ara..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg mb-3"
                value={extraCurrencySearch}
                onChange={(e) => setExtraCurrencySearch(e.target.value)}
              />

              <div className="border border-gray-300 rounded-lg max-h-64 overflow-y-auto" data-testid="extra-currency-list">
                {(() => {
                  const available = filteredExtraCurrencies.filter(c => !extraCurrencies.includes(c.code));
                  if (available.length === 0) {
                    return (
                      <div className="px-4 py-8 text-center text-gray-500">
                        {extraCurrencySearch ? 'Sonuç bulunamadı.' : 'Tüm para birimleri seçildi.'}
                      </div>
                    );
                  }
                  const canSelect = extraCurrencies.length < 4;
                  const renderRow = (currency) => (
                    <div
                      key={currency.code}
                      className={`px-4 py-3 cursor-pointer hover:bg-gray-50 border-b last:border-b-0 ${
                        !canSelect ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                      onClick={() => {
                        if (!canSelect) return;
                        setExtraCurrencies([...extraCurrencies, currency.code]);
                        setExtraCurrencySearch('');
                      }}
                      data-testid={`extra-currency-option-${currency.code}`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-xl leading-none" aria-hidden>{getCurrencyFlag(currency.code)}</span>
                        <span className="font-semibold text-gray-900">{currency.symbol} {currency.code}</span>
                        <span className="text-gray-600 text-sm">{currency.name}</span>
                      </div>
                    </div>
                  );
                  // Search aktifken popüler/diğer ayrımı yapılmaz — kullanıcı
                  // aradığı sonuçları filtre sırasında görmek ister.
                  if (extraCurrencySearch.trim()) {
                    return available.map(renderRow);
                  }
                  const { popular, rest } = splitCurrenciesByPopularity(available);
                  return (
                    <>
                      {popular.map(renderRow)}
                      {popular.length > 0 && rest.length > 0 && (
                        <div className="px-4 py-1.5 bg-gray-50 text-[10px] uppercase tracking-wider text-gray-400 font-medium border-b">
                          Diğer para birimleri
                        </div>
                      )}
                      {rest.map(renderRow)}
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        );

      case 3: // Şirket Bilgileri
        return (
          <div className="space-y-4">
            <div className="text-center mb-6">
              <div className="text-5xl mb-3">🏢</div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Şirket Bilgileri
              </h3>
              <p className="text-gray-600 text-sm">
                Temel bilgiler zorunlu, diğerleri opsiyoneldir
              </p>
            </div>

            <div className="max-w-2xl mx-auto space-y-6">
              {/* Kişisel Bilgiler Bölümü */}
              <div className="bg-gray-50 p-4 rounded-lg space-y-4">
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Kişisel Bilgiler</h4>
                
                {/* Adı Soyadı */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Adı Soyadı <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder="Örn: Ahmet Yılmaz"
                    required
                  />
                </div>

                {/* Email (Read-only) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    E-posta Adresi
                  </label>
                  <input
                    type="email"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed"
                    value={email}
                    disabled
                    readOnly
                  />
                  <p className="text-xs text-gray-500 mt-1">Kayıt olurken kullandığınız e-posta adresi</p>
                </div>

                {/* Telefon */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Telefon Numarası <span className="text-red-500">*</span>
                  </label>
                  <div className="flex space-x-2">
                    {/* Country Code Dropdown */}
                    <select
                      className="w-40 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                      value={phoneCountryCode}
                      onChange={(e) => setPhoneCountryCode(e.target.value)}
                    >
                      {phoneCodes.map((item) => (
                        <option key={item.code} value={item.code}>
                          {item.flag} {item.code}
                        </option>
                      ))}
                    </select>
                    
                    {/* Phone Number Input */}
                    <div className="flex-1">
                      <input
                        type="tel"
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 ${
                          phoneError ? 'border-red-500' : 'border-gray-300'
                        }`}
                        value={phoneNumber}
                        onChange={handlePhoneChange}
                        onBlur={handlePhoneBlur}
                        placeholder="XXX XXX XX XX"
                        required
                      />
                      {phoneError && (
                        <p className="text-red-600 text-xs mt-1">{phoneError}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Adres */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Adres
                </label>
                <textarea
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                  rows={2}
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="Mahalle, Sokak, No, Daire..."
                />
              </div>

              {/* Posta Kodu, Şehir, Ülke */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Posta Kodu
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                    value={postalCode}
                    onChange={(e) => setPostalCode(e.target.value)}
                    placeholder="34000"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ülke
                  </label>
                  <select
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                    value={country}
                    onChange={(e) => {
                      setCountry(e.target.value);
                      setCity(''); // Reset city when country changes
                    }}
                  >
                    <option value="">Ülke seçiniz ({countries.length})</option>
                    {countries.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Şehir
                  </label>
                  {cities.length > 0 ? (
                    <select
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      disabled={!country || loadingCities}
                    >
                      <option value="">
                        {loadingCities ? 'Yükleniyor...' : 'Şehir seçiniz'}
                      </option>
                      {cities.map((c) => (
                        <option key={c.id} value={c.name}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      placeholder="Şehir girin (örn: Dubai, Berlin)"
                      disabled={!country || loadingCities}
                    />
                  )}
                </div>
              </div>

              {/* Kurumsal Fatura Checkbox */}
              <div className="border-t pt-4 mt-6">
                <div className="flex items-center space-x-3 mb-4">
                  <input
                    type="checkbox"
                    id="corporateInvoice"
                    className="w-5 h-5 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                    checked={corporateInvoice}
                    onChange={(e) => setCorporateInvoice(e.target.checked)}
                  />
                  <label htmlFor="corporateInvoice" className="text-sm font-medium text-gray-700 cursor-pointer">
                    Kurumsal Fatura istiyorum
                  </label>
                </div>

                {/* Kurumsal Fatura Alanları */}
                {corporateInvoice && (
                  <div className="space-y-4 pl-8 border-l-2 border-teal-200">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Fatura Şirket Adı <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                          value={invoiceCompanyName}
                          onChange={(e) => setInvoiceCompanyName(e.target.value)}
                          placeholder="Fatura için şirket adı"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Vergi No <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          maxLength={10}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                          value={invoiceTaxNumber}
                          onChange={(e) => {
                            // Yalnızca rakam kabul et; harf, boşluk, tire, nokta
                            // dahil özel karakterler otomatik silinir. Maks 10
                            // basamak (Türkiye vergi kimlik numarası kuralı).
                            const digitsOnly = e.target.value.replace(/\D/g, '').slice(0, 10);
                            setInvoiceTaxNumber(digitsOnly);
                          }}
                          placeholder="10 basamaklı vergi numarası"
                          data-testid="tax-number-input"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          {invoiceTaxNumber.length}/10 basamak — sadece rakam
                        </p>
                      </div>
                    </div>

                    {/* Mevcut Adresi Kullan Checkbox */}
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        id="useSameAddress"
                        className="w-5 h-5 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                        checked={useSameAddress}
                        onChange={(e) => setUseSameAddress(e.target.checked)}
                      />
                      <label htmlFor="useSameAddress" className="text-sm font-medium text-gray-700 cursor-pointer">
                        Mevcut adresi fatura adresi olarak kullan
                      </label>
                    </div>

                    {/* Fatura Adresi Alanları */}
                    {!useSameAddress && (
                      <div className="space-y-4 bg-gray-50 p-4 rounded-lg">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Fatura Adresi <span className="text-red-500">*</span>
                          </label>
                          <textarea
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                            rows={2}
                            value={invoiceAddress}
                            onChange={(e) => setInvoiceAddress(e.target.value)}
                            placeholder="Fatura adresi..."
                          />
                        </div>

                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Ülke <span className="text-red-500">*</span>
                            </label>
                            <select
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                              value={invoiceCountry}
                              onChange={(e) => {
                                setInvoiceCountry(e.target.value);
                                setInvoiceCity(''); // Reset city when country changes
                              }}
                            >
                              <option value="">Ülke seçiniz</option>
                              {countries.map((c) => (
                                <option key={c.id} value={c.id}>
                                  {c.name}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Şehir <span className="text-red-500">*</span>
                            </label>
                            {invoiceCities.length > 0 ? (
                              <select
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                                value={invoiceCity}
                                onChange={(e) => setInvoiceCity(e.target.value)}
                                disabled={!invoiceCountry || loadingInvoiceCities}
                              >
                                <option value="">
                                  {loadingInvoiceCities ? 'Yükleniyor...' : 'Şehir seçiniz'}
                                </option>
                                {invoiceCities.map((c) => (
                                  <option key={c.id} value={c.name}>
                                    {c.name}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <input
                                type="text"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                                value={invoiceCity}
                                onChange={(e) => setInvoiceCity(e.target.value)}
                                placeholder="Şehir girin"
                                disabled={!invoiceCountry || loadingInvoiceCities}
                              />
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Posta Kodu <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                              value={invoicePostalCode}
                              onChange={(e) => setInvoicePostalCode(e.target.value)}
                              placeholder="34000"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case 4: // Kullanıcı Ekleme
        return (
          <div className="space-y-4">
            <div className="text-center mb-6">
              <div className="text-5xl mb-3">👥</div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Takım Arkadaşlarınızı Davet Edin
              </h3>
              <p className="text-gray-600 text-sm">
                İsterseniz şimdi ekip üyelerinizi ekleyebilir veya atlayabilirsiniz
              </p>
            </div>

            <div className="max-w-2xl mx-auto">
              {/* Invite Form */}
              <div className="bg-white border border-gray-300 rounded-lg p-6 mb-4">
                <h4 className="font-semibold text-gray-900 mb-4">Yeni Kullanıcı Davet Et</h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      E-posta Adresi
                    </label>
                    <input
                      type="email"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                      placeholder="ornek@email.com"
                      value={inviteEmail}
                      onChange={(e) => {
                        setInviteEmail(e.target.value);
                        setInviteError('');
                      }}
                      onKeyPress={(e) => e.key === 'Enter' && handleInviteUser()}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Rol
                    </label>
                    <select
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                      value={inviteRole}
                      onChange={(e) => setInviteRole(e.target.value)}
                    >
                      <option value="">Rol seçin</option>
                      {roles.filter(r => r.name !== 'Süper Admin').map((role) => (
                        <option key={role.id} value={role.id}>
                          {role.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <button
                    onClick={handleInviteUser}
                    disabled={inviting || !inviteEmail || !inviteRole}
                    className="w-full px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {inviting ? 'Gönderiliyor...' : '📧 Davet Gönder'}
                  </button>
                </div>
              </div>

              {/* Error Message */}
              {inviteError && (
                <div className="bg-red-50 border-2 border-red-500 rounded-lg p-4 mb-4">
                  <div className="flex items-start space-x-3">
                    <div className="text-red-600 text-2xl">⚠️</div>
                    <div className="flex-1">
                      <p className="text-red-800 font-semibold text-base">
                        {inviteError}
                      </p>
                    </div>
                    <button
                      onClick={() => setInviteError('')}
                      className="text-red-600 hover:text-red-800"
                    >
                      <XIcon className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              )}

              {/* Invited Users List */}
              {invitedUsers.length > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h4 className="font-semibold text-green-900 mb-3 flex items-center">
                    <CheckIcon className="w-5 h-5 mr-2" />
                    Davet Gönderildi ({invitedUsers.length})
                  </h4>
                  <div className="space-y-2">
                    {invitedUsers.map((invitedUser, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-white p-3 rounded border border-green-200">
                        <div>
                          <p className="font-medium text-gray-900">{invitedUser.email}</p>
                          <p className="text-sm text-gray-600">{invitedUser.role}</p>
                        </div>
                        <CheckIcon className="w-5 h-5 text-green-600" />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Info Box */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                <div className="flex items-start space-x-2">
                  <div className="text-blue-600 mt-0.5">ℹ️</div>
                  <div className="text-sm text-blue-800">
                    <p><strong>Davet e-postaları gönderildi!</strong> Davet edilen kullanıcılar e-postalarındaki linke tıklayıp kaydolduklarında aktif kullanıcı olarak sisteme ekleneceklerdir. Bu kullanıcılar "Bekleyen Davetler" bölümünde görünecektir.</p>
                    <p className="mt-2">Daha sonra Ayarlar → Kullanıcı Yönetimi'nden de yeni kullanıcı davet edebilirsiniz.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 5: // Kullanıcı Tipleri
        return (
          <div className="space-y-4">
            <div className="text-center mb-6">
              <div className="text-5xl mb-3">🏷️</div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Kullanıcı Tipleri
              </h3>
              <p className="text-gray-600 text-sm">
                Aşağıdaki roller şirketiniz için otomatik oluşturulmuştur
              </p>
            </div>
            <div className="max-w-3xl mx-auto">
              <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 mb-3 text-sm text-teal-800">
                <strong>{DEFAULT_USER_TYPES.length}+ hazır kullanıcı tipi</strong> eklendi. Kullanıcı davet ederken bu listeden seçebilir, dilediğiniz zaman
                <span className="mx-1 font-medium">Ayarlar → Kullanıcı Tipleri</span>
                bölümünden yeni tip ekleyip mevcutları düzenleyebilirsiniz.
              </div>
              <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-3 bg-white">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-1.5" data-testid="default-user-types-list">
                  {DEFAULT_USER_TYPES.map((t) => (
                    <div key={t} className="text-sm text-gray-700 flex items-center gap-2 py-0.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-teal-500 flex-shrink-0" />
                      <span className="truncate">{t}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      case 6: // Pozisyon Hiyerarşisi
        return (
          <div className="space-y-4">
            <div className="text-center mb-6">
              <div className="text-5xl mb-3">📊</div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Pozisyon Hiyerarşisi
              </h3>
              <p className="text-gray-600 text-sm">
                Varsayılan hiyerarşi otomatik oluşturulmuştur
              </p>
            </div>

            <div className="text-center text-gray-500 py-8">
              <p className="mb-4">Varsayılan pozisyon hiyerarşisi sisteme eklenmiştir.</p>
              <p className="text-sm">Daha sonra Ayarlar'dan düzenleyebilirsiniz.</p>
            </div>
          </div>
        );

      case 7: // Kullanıcı Yetkileri
        return (
          <div className="space-y-4">
            <div className="text-center mb-6">
              <div className="text-5xl mb-3">🔐</div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Kullanıcı Yetkileri
              </h3>
              <p className="text-gray-600 text-sm">
                Rollere göre aşağıdaki yetki kategorileri hazırlandı
              </p>
            </div>
            <div className="max-w-3xl mx-auto">
              <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 mb-3 text-sm text-teal-800">
                <strong>Admin</strong> rolü tüm yetkilere sahip başlar; diğer roller
                <span className="mx-1">temel modüller + kendi ekibinin onay/kapama hakkı</span>
                ile başlar. Ayarlar → Kullanıcı Yetkileri'nden her rol için tek tek düzenleyebilirsiniz.
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="default-permission-categories">
                {DEFAULT_PERMISSION_CATEGORIES.map((cat) => (
                  <div key={cat.key} className="border border-gray-200 rounded-lg p-3 bg-white">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xl">{cat.icon}</span>
                      <div className="font-semibold text-gray-900 text-sm">{cat.label}</div>
                    </div>
                    <ul className="space-y-1">
                      {cat.items.map((item) => (
                        <li key={item} className="text-xs text-gray-600 flex items-start gap-1.5">
                          <span className="text-teal-500 mt-0.5">✓</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const currentStepData = steps[currentStep - 1];

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] flex flex-col shadow-2xl"
      >
        {/* Header */}
        {currentStep > 0 && (
          <div className="px-6 pt-6 pb-4 border-b flex-shrink-0">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  Kurulum Sihirbazı 🎉
                </h2>
                <p className="text-gray-600 text-sm mt-1">
                  Adım {currentStep} / {totalSteps}: {steps[currentStep - 1]?.title}
                </p>
              </div>
              <button
                onClick={() => {
                  if (window.confirm('Kurulum sihirbazını sonra tamamlamak istiyor musunuz?')) {
                    navigate('/dashboard');
                  }
                }}
                className="text-sm text-gray-500 hover:text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-100"
              >
                Sonra Tamamla
              </button>
            </div>

            {/* Progress bar */}
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-teal-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(currentStep / totalSteps) * 100}%` }}
              />
            </div>

            {/* Step labels */}
            <div className="flex justify-between mt-2">
              {steps.map((step, index) => (
                <div
                  key={step.id}
                  className={`text-xs ${
                    index + 1 === currentStep
                      ? 'text-teal-600 font-semibold'
                      : index + 1 < currentStep
                      ? 'text-green-600'
                      : 'text-gray-400'
                  }`}
                >
                  {step.title}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ x: 20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: -20, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              {renderStepContent()}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Footer */}
        {currentStep > 0 && (
          <div className="px-6 py-4 border-t bg-gray-50 rounded-b-xl flex-shrink-0">
            <div className="flex items-center justify-between">
              <Button
                onClick={handleBack}
                variant="outline"
                className="flex items-center space-x-2"
              >
                <ArrowLeftIcon className="w-4 h-4" />
                <span>Geri</span>
              </Button>

              <div className="flex items-center space-x-3">
                {/* Atla butonu — sadece "Kullanıcı Ekleme" adımında (step 4)
                    gösterilir. Kullanıcı Tipleri (5), Pozisyon Hiyerarşisi
                    (6) ve Kullanıcı Yetkileri (7) sadece bilgilendirme
                    ekranlarıdır; "İleri" ile geçilir, Atla anlamlı değil. */}
                {currentStep === 4 && !steps[currentStep - 1]?.required && (
                  <Button
                    onClick={handleSkipStep}
                    variant="outline"
                    className="text-gray-600"
                    data-testid="onboarding-skip-btn"
                  >
                    Atla
                  </Button>
                )}
                <Button
                  onClick={handleNext}
                  className="bg-teal-600 hover:bg-teal-700 text-white flex items-center space-x-2"
                  disabled={loading}
                >
                  {loading ? (
                    <span>Tamamlanıyor...</span>
                  ) : currentStep === totalSteps ? (
                    <>
                      <CheckIcon className="w-4 h-4" />
                      <span>Tamamla</span>
                    </>
                  ) : (
                    <>
                      <span>İleri</span>
                      <ArrowRightIcon className="w-4 h-4" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
      </motion.div>

      {/* Sayfadan Ayrılma Onay Modal'ı — minimal & merkezli tasarım.
          Kaydet ve Çık ilerlemeyi localStorage'a yazar; kullanıcı geri
          döndüğünde useEffect draft'ı restore eder. */}
      {leaveModal.open && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          data-testid="onboarding-leave-modal"
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6 text-center">
            <h3 className="text-base font-semibold text-gray-900 mb-2">
              Sayfadan ayrılmak üzeresiniz
            </h3>
            <p className="text-sm text-gray-500 leading-relaxed mb-6">
              Onboarding tamamlanmadı. İlerlemenizi kaydedip sonra devam
              edebilir, yoksa tümüyle çıkabilirsiniz.
            </p>
            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={saveDraftAndLeave}
                className="w-full px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium transition"
                data-testid="onboarding-leave-save"
              >
                Kaydet ve Çık
              </button>
              <button
                type="button"
                onClick={cancelLeave}
                className="w-full px-4 py-2 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 text-sm font-medium transition"
                data-testid="onboarding-leave-cancel"
              >
                İptal
              </button>
              <button
                type="button"
                onClick={confirmLeave}
                className="w-full px-4 py-2 rounded-lg text-red-600 hover:bg-red-50 text-xs font-medium transition"
                data-testid="onboarding-leave-confirm"
              >
                Kaydetmeden çık
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OnboardingFullPage;
